import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  fetchBalance,
  getInitData,
  loginDev,
  loginWithInitData,
  multipliers,
  play,
  takeTokenFromUrl,
  type PlinkoRound,
} from './api'
import { Board } from './Board'
import { goLobby } from './lobby'
import { SPRING_POP, SPRING_SETTLE } from '../../shared/motion.ts'
import { useCountUp } from './useCountUp'
import { GameHeader } from './components/GameHeader'
import { BottomSheetDrawer, type SheetTab } from './components/BottomSheetDrawer'
import { BetDock } from './components/BetDock'
import brandMark from '../../shared/assets/round6-snowfold-atlas-mark-v1.png'
import plinkoWorld from '../../shared/assets/round6-plinko-snowfold-stage-v1.webp'

type Risk = 'low' | 'medium' | 'high'

export default function App() {
  const { t, i18n } = useTranslation()
  useEffect(() => {
    document.title = t('title')
  }, [t, i18n.language])

  const [token, setToken] = useState<string | null>(() => takeTokenFromUrl())
  const [balance, setBalance] = useState(0)
  const [authLoading, setAuthLoading] = useState(true)
  const [authError, setAuthError] = useState<string | null>(null)
  const [authRetry, setAuthRetry] = useState(0)
  const [bet, setBet] = useState(100)
  const [risk, setRisk] = useState<Risk>('medium')
  const [table, setTable] = useState<number[]>([])
  const [tableRisk, setTableRisk] = useState<Risk | null>(null)
  const [multipliersLoading, setMultipliersLoading] = useState(false)
  const [multipliersError, setMultipliersError] = useState<string | null>(null)
  const [last, setLast] = useState<PlinkoRound | null>(null)
  const [animKey, setAnimKey] = useState(0)
  const [busy, setBusy] = useState(false)
  const [resultVisible, setResultVisible] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [balanceWarning, setBalanceWarning] = useState<string | null>(null)
  const [balanceRefreshing, setBalanceRefreshing] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [showChips, setShowChips] = useState(false)

  const multipliersRequestRef = useRef(0)
  const playInFlightRef = useRef(false)
  const balanceRefreshRef = useRef(false)
  const boardCompleteRef = useRef(false)
  const shownBalance = Math.round(useCountUp(balance))
  const shownMultiplier = useCountUp(resultVisible ? last?.multiplier ?? 0 : 0)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    let cancelled = false
    setAuthLoading(true)
    setAuthError(null)
    void (async () => {
      try {
        if (token) {
          const { balance: bal } = await fetchBalance(token)
          if (!cancelled) {
            setBalance(bal)
            setAuthLoading(false)
          }
          return
        }
        const init = await getInitData()
        const res = init ? await loginWithInitData(init) : await loginDev()
        if (cancelled) return
        setToken(res.token)
        setBalance(res.user.balance)
        setAuthLoading(false)
      } catch (e) {
        if (!cancelled) {
          void e
          setAuthError(t('authFailed'))
          setAuthLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authRetry])

  const loadMultipliers = useCallback(
    async (tok: string, r: Risk) => {
      const reqId = ++multipliersRequestRef.current
      setMultipliersLoading(true)
      setMultipliersError(null)
      setTable([])
      setTableRisk(null)
      try {
        const res = await multipliers(tok, r)
        if (reqId !== multipliersRequestRef.current) return
        setTable(res.multipliers)
        setTableRisk(r)
      } catch {
        if (reqId !== multipliersRequestRef.current) return
        setMultipliersError(t('oddsLoadFailed'))
      } finally {
        if (reqId === multipliersRequestRef.current) {
          setMultipliersLoading(false)
        }
      }
    },
    [t],
  )

  useEffect(() => {
    if (token) void loadMultipliers(token, risk)
  }, [token, risk, loadMultipliers])

  const onRiskChange = (r: Risk) => {
    if (busy || r === risk) return
    setRisk(r)
    setError(null)
    setLast(null)
    setResultVisible(false)
  }

  const betIsValid = bet >= 10 && bet <= balance
  const hasCurrentTable = tableRisk === risk && table.length > 0
  const canPlay =
    !busy &&
    !balanceWarning &&
    !balanceRefreshing &&
    !multipliersLoading &&
    !multipliersError &&
    betIsValid &&
    hasCurrentTable

  const refreshBalance = useCallback(async () => {
    if (!token || balanceRefreshRef.current) return
    balanceRefreshRef.current = true
    setBalanceRefreshing(true)
    try {
      const { balance: bal } = await fetchBalance(token)
      setBalance(bal)
      setBalanceWarning(null)
      return true
    } catch {
      setBalanceWarning((current) => current ?? t('balanceRefreshFailed'))
      return false
    } finally {
      balanceRefreshRef.current = false
      setBalanceRefreshing(false)
    }
  }, [t, token])

  const onPlay = async () => {
    if (!token || !canPlay || playInFlightRef.current) return
    // React state updates are asynchronous; take a synchronous lock so a
    // double tap cannot submit two independent wagers before `busy` renders.
    playInFlightRef.current = true
    boardCompleteRef.current = false
    setBusy(true)
    setError(null)
    setBalanceWarning(null)
    setResultVisible(false)
    setLast(null)
    try {
      const res = await play(token, bet, risk)
      setLast(res.round)
      setAnimKey((k) => k + 1)
      await refreshBalance()
      if (boardCompleteRef.current) setBusy(false)
    } catch {
      setBalanceWarning(t('balanceOutcomeUnknown'))
      setBusy(false)
    } finally {
      playInFlightRef.current = false
    }
  }

  const onBoardComplete = useCallback(() => {
    boardCompleteRef.current = true
    setResultVisible(true)
    if (!balanceRefreshRef.current) setBusy(false)
  }, [])

  if (authLoading) {
    return (
      <div className="center" role="status" aria-live="polite">
        <div className="spinner" aria-hidden />
        <p>{t('connecting')}</p>
      </div>
    )
  }
  if (authError || !token) {
    return (
      <div className="center" role="alert">
        <p className="error">{authError ?? t('unauthorized')}</p>
        <button type="button" className="btn-lobby" onClick={() => setAuthRetry((n) => n + 1)}>
          {t('retryLogin')}
        </button>
        <button type="button" className="btn-lobby" onClick={() => goLobby(token)}>
          {t('backLobby')}
        </button>
      </div>
    )
  }

  const riskLabel = (r: Risk) => {
    if (r === 'low') return t('riskLow')
    if (r === 'high') return t('riskHigh')
    return t('riskMedium')
  }

  const settled = Boolean(last) && resultVisible
  const isProfit = Boolean(last && last.multiplier >= 1)

  const drawerTabs: SheetTab[] = [
    {
      id: 'rules',
      label: t('rules'),
      content: (
        <div style={{ lineHeight: 1.6 }}>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1rem', color: '#fff' }}>{t('rulesTitle')}</h3>
          <p style={{ color: 'var(--text-secondary)' }}>{t('rulesBody')}</p>
        </div>
      ),
    },
  ]

  // Tier 1: Risk selector
  const parameterSlot = (
    <div className="bet-dock-segmented">
      {(['low', 'medium', 'high'] as const).map((r) => (
        <button
          key={r}
          type="button"
          disabled={busy}
          className={`bet-dock-segment-btn${risk === r ? ' active' : ''}`}
          aria-pressed={risk === r}
          onClick={() => onRiskChange(r)}
        >
          {riskLabel(r)}
        </button>
      ))}
    </div>
  )

  const QUICK = [10, 50, 100, 250, 500]

  // Tier 2: Stake input & Collapsible Quick chips
  const amountSlot = (
    <div className="bet-dock-tier-amount">
      <div className="bet-dock-amount-main-row">
        <div className="bet-dock-input-group">
          <label htmlFor="plinko-bet">{t('bet')}</label>
          <input
            id="plinko-bet"
            type="number"
            min={10}
            max={balance}
            value={bet}
            disabled={busy}
            onChange={(e) => setBet(Math.max(0, Number(e.target.value) || 0))}
          />
        </div>
        <div className="bet-dock-quick-multipliers">
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, Math.floor(bet / 2)))}
          >
            1/2
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, Math.min(balance, bet * 2)))}
          >
            2X
          </button>
          <button
            type="button"
            className="bet-dock-multiplier-btn"
            disabled={busy}
            onClick={() => setBet(Math.max(10, balance))}
          >
            {t('max')}
          </button>
          <button
            type="button"
            className={`bet-dock-chip-toggle${showChips ? ' active' : ''}`}
            disabled={busy}
            onClick={() => setShowChips(!showChips)}
            title={t('quickChips')}
            aria-label={t('quickChips')}
            aria-expanded={showChips}
            aria-controls="plinko-quick-chips"
          >
            {showChips ? '▲' : '⚡'}
          </button>
        </div>
      </div>
      {showChips && (
        <div id="plinko-quick-chips" className="bet-dock-collapsible-chips">
          {QUICK.map((q) => (
            <button
              key={q}
              type="button"
              className="bet-dock-preset-chip"
              disabled={busy}
              onClick={() => {
                setBet(q)
                setShowChips(false)
              }}
            >
              {q}
            </button>
          ))}
        </div>
      )}
    </div>
  )

  // Tier 3: Primary Action CTA
  const ctaSlot = (
    <motion.button
      type="button"
      className={`bet-dock-cta-btn state-ready${settled && isProfit ? ' state-win' : ''}`}
      disabled={
        multipliersError || balanceWarning
          ? busy || multipliersLoading || balanceRefreshing
          : !canPlay
      }
      onClick={() => {
        if (balanceWarning) void refreshBalance()
        else if (multipliersError) void loadMultipliers(token, risk)
        else void onPlay()
      }}
      whileTap={reduceMotion ? undefined : { scale: 0.98 }}
      transition={SPRING_SETTLE}
    >
      {busy
        ? t('dropping')
        : balanceRefreshing
          ? t('refreshingBalance')
          : balanceWarning
            ? t('retryBalance')
        : multipliersLoading
          ? t('oddsLoading')
          : multipliersError
            ? t('retryOdds')
            : `${t('drop')} (${bet})`}
    </motion.button>
  )

  const metaSlot = (
    <>
      <span>{t('balance', { amount: shownBalance })}</span>
      {settled && last && (
        <span style={{ color: isProfit ? 'var(--color-win)' : 'var(--color-danger)' }}>
          {isProfit ? t('resultWin') : t('resultLose')} · {last.multiplier.toFixed(2)}x ({last.payout})
        </span>
      )}
      {balanceWarning && (
        <span role="status" aria-live="polite" style={{ color: 'var(--color-warning, #f7c66b)' }}>
          {balanceWarning}
        </span>
      )}
    </>
  )

  return (
    <div className="app app-single-viewport" data-game="plinko">
      <GameHeader
        title={t('title')}
        brandSrc={brandMark}
        lobbyLabel={t('backLobby')}
        onLobby={() => goLobby(token)}
        balance={shownBalance}
        balanceLabel={t('balance', { amount: shownBalance })}
        onOpenRules={() => setSheetOpen(true)}
      />

      <div className="game-stage-viewport">
        <section
          className="plinko-stage"
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <img className="game-world-backdrop" src={plinkoWorld} alt="" aria-hidden />
          <div className="plinko-board-scale">
            <Board
              rows={last?.rows ?? 12}
              path={last?.path ?? null}
              slot={last?.slot ?? null}
              multipliers={table}
              animKey={animKey}
              onComplete={onBoardComplete}
            />
          </div>
          <AnimatePresence>
            {settled && last ? (
              <motion.div
                className="plinko-result-overlay"
                role="status"
                aria-live="polite"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  key={`${last.slot}-${animKey}`}
                  className={`plinko-result-card${isProfit ? ' is-win' : ' is-loss'}`}
                  aria-labelledby="plinko-result-title"
                  initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.88, y: 16 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.94 }}
                  transition={reduceMotion ? { duration: 0 } : SPRING_POP}
                >
                  <p className="plinko-result-kicker">{t('slot', { n: last.slot })}</p>
                  <h2 id="plinko-result-title">
                    {isProfit ? t('resultWin') : t('resultLose')}
                  </h2>
                  <strong className="plinko-result-mult">{shownMultiplier.toFixed(2)}x</strong>
                  <p className="plinko-result-pay">
                    {t('payout', { amount: last.payout.toLocaleString('en-US') })}
                  </p>
                </motion.div>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </section>
      </div>

      <BetDock
        className="plinko-bet-dock"
        parameterSlot={parameterSlot}
        amountSlot={amountSlot}
        ctaSlot={ctaSlot}
        metaSlot={metaSlot}
        error={error ?? multipliersError}
      />

      <BottomSheetDrawer
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        tabs={drawerTabs}
        closeLabel={t('close')}
      />
    </div>
  )
}
